import React, { useState, useEffect, useContext, useRef } from "react";
import { Container, Row, Col, Form, InputGroup } from "react-bootstrap";
import {
  FiSearch,
  FiSend,
  FiPaperclip,
  FiArrowLeft,
  FiPhone,
  FiMoreVertical,
  FiMessageSquare,
} from "react-icons/fi";
import axios from "axios";
import Swal from "sweetalert2";
import MyContext from "../../../context/MyContext";
import { BsCheck2All } from "react-icons/bs";
import DeliveryPanelLayout from "../../../layouts/DeliveryPanelLayout";
import { MessagesPageSkeleton } from "../../../components/general/skeleton/SkeletonBlock";
import "../../../assets/css/delivery/deliveryMessages/DeliveryMessages.css";

// ─── Messages Page ─────────────────────────────────────────────────────────────
const DeliveryMessages = () => {
  // ── States ── (each owns a single concern)
  const { baseUrl, baseUrlForFiles, loggedInDeliveryMan } =
    useContext(MyContext);
  const [loading, setLoading] = useState(true);

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [messageText, setMessageText] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);
  const [showChatOnMobile, setShowChatOnMobile] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const formatTime = (datetime) => {
    if (!datetime) return "";
    const date = new Date(datetime);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };
  // ── Resize listener ──
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 991);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [messages]);
  // ── Derived values ──
  const filteredConversations = conversations.filter((conv) =>
    conv.kmcustomer?.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const viewAllConversations = () => {
    const formData = new FormData();


    axios
      .post(
        `${baseUrl}/delivery/conversations/viewAllConversations`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      )
      .then((response) => {
        if (response.data.error) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setConversations(response.data.conversations || []);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };
  useEffect(() => {
    viewAllConversations();
  }, []);
  const viewAllMessages = (conversationId) => {
    const formData = new FormData();

    formData.append("kmconversation_id", conversationId);

    axios
      .post(`${baseUrl}/delivery/chat/viewAllMessages`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        if (response.data.error) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setMessages(response.data.messages || []);
        }
      });
  };
  const sendMessage = () => {
    if (!messageText.trim() && !attachedFile) return;

    if (!selectedChat) return;

    const formData = new FormData();

    formData.append("kmconversation_id", selectedChat.id);

    formData.append("send_by", "deliveryman");

    formData.append("message", messageText);

    formData.append("image", attachedFile);

    axios
      .post(`${baseUrl}/delivery/chat/sendMessage`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        if (response.data.error) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setMessageText("");
          setAttachedFile(null);

          if (fileInputRef.current) fileInputRef.current.value = "";

          viewAllMessages(selectedChat.id);

          viewAllConversations();
        }
      });
  };

  // ── Loading ───────────────────────────────────────────────────
  if (loading)
    return (
      <DeliveryPanelLayout>
        <MessagesPageSkeleton />
      </DeliveryPanelLayout>
    );

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <DeliveryPanelLayout>
      <Container fluid className="del-msg-wrapper">
        <Row className="g-0 del-msg-row">
          {/* ── Conversation List ── */}
          <Col
            lg={4}
            className={[
              "del-msg-list",
              isMobile && showChatOnMobile ? "del-msg-list--hidden" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div className="del-msg-list-header">
              <h5 className="del-msg-title">Messages</h5>
              <InputGroup className="del-msg-search-box">
                <InputGroup.Text className="del-msg-search-icon">
                  <FiSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="del-msg-search-input"
                />
              </InputGroup>
            </div>

            <div className="del-msg-conversations">
              {filteredConversations.map((conv) => (
                <div
                  key={conv.id}
                  className={[
                    "del-msg-conv-item",
                    conv.id === selectedChat?.id
                      ? "del-msg-conv-item--active"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => {
                    setSelectedChat(conv);

                    viewAllMessages(conv.id);
                    if (isMobile) setShowChatOnMobile(true);
                  }}
                >
                  <div className="del-msg-avatar-wrap">
                    <div className="del-msg-avatar">
                      {conv.kmcustomer?.name?.charAt(0)}
                    </div>
                  </div>
                  <div className="del-msg-conv-info">
                    <div className="del-msg-conv-top">
                      <span className="del-msg-conv-name">
                        {conv.kmcustomer?.name}
                      </span>
                      <span className="del-msg-conv-time">
                        {formatTime(conv.updated_at)}
                      </span>
                    </div>
                    <div className="del-msg-conv-bottom">
                      <span className="del-msg-conv-preview">
                        {conv.lastMessage}
                      </span>
                      {conv.status === "unread" && (
                        <span className="del-msg-unread-badge">new</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Col>

          {/* ── Active Chat ── */}
          <Col
            lg={8}
            className={[
              "del-msg-chat",
              isMobile && !showChatOnMobile ? "del-msg-chat--hidden" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {selectedChat ? (
              <>
                {/* Chat Header */}
                <div className="del-msg-chat-header">
                  {isMobile && (
                    <button
                      className="del-msg-back-btn"
                      onClick={() => setShowChatOnMobile(false)}
                    >
                      <FiArrowLeft />
                    </button>
                  )}
                  <div className="del-msg-avatar-wrap">
                    <div className="del-msg-avatar">
                      {selectedChat.initials}
                    </div>
                    {selectedChat.online && (
                      <span className="del-msg-online-dot" />
                    )}
                  </div>
                  <div className="del-msg-chat-header-info">
                    <span className="del-msg-chat-header-name">
                      {selectedChat.name}
                    </span>
                  </div>
                  <div className="del-msg-chat-header-actions">
                    <button className="del-msg-icon-btn">
                      <FiPhone />
                    </button>
                    <button className="del-msg-icon-btn">
                      <FiMoreVertical />
                    </button>
                  </div>
                </div>

                {/* Chat Body */}
                <div className="del-msg-chat-body">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={[
                        "del-msg-bubble",
                        msg.send_by === "deliveryman"
                          ? "del-msg-bubble--sent"
                          : "del-msg-bubble--received",
                      ].join(" ")}
                    >
                      <p className="del-msg-bubble-text">{msg.message}</p>
                      {msg.image && (
                        <img
                          src={`${baseUrlForFiles}/${msg.image}`}
                          alt=""
                          className="del-msg-image"
                        />
                      )}
                      <span className="del-msg-bubble-time">
                        {formatTime(msg.created_at)}

                        {msg.sender === "deliveryMan" && (
                          <BsCheck2All className="del-msg-status-icon" />
                        )}
                      </span>
                    </div>
                  ))}
                </div>
                {attachedFile && (
                  <div className="del-msg-file-preview">
                    <span>📎 {attachedFile.name}</span>

                    <button
                      onClick={() => {
                        setAttachedFile(null);

                        if (fileInputRef.current)
                          fileInputRef.current.value = "";
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}
                {/* Chat Footer */}
                <div className="del-msg-chat-footer">
                  <button
                    className="del-msg-icon-btn"
                    onClick={() => fileInputRef.current.click()}
                  >
                    {" "}
                    <FiPaperclip />
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={(e) => setAttachedFile(e.target.files[0])}
                  />
                  <Form.Control
                    type="text"
                    placeholder="Type a message..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    className="del-msg-input"
                  />
                  <button
                    className="del-msg-send-btn"
                    onClick={sendMessage}
                    disabled={!messageText.trim() && !attachedFile}
                  >
                    {" "}
                    <FiSend />
                  </button>
                </div>
              </>
            ) : (
              <div className="del-msg-empty-state">
                <FiMessageSquare className="del-msg-empty-icon" />
                <p>Select a conversation to start chatting</p>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </DeliveryPanelLayout>
  );
};

export default DeliveryMessages;
